import { useState } from "react";
import inputValidityChecker from "../../../../../utils/inputValidityChecker";
import { useAuth } from "../../../../auth/hooks";
import { UserRole } from "../../../../user/types/user.types";
import { ChargePointPatch } from "../../../types/stations.types";
import NewChargePointFormView from "./NewChargePointFomView/NewChargePointFormView";
import useChargePoints from "../../../hooks/useChargePoint";
import { extractAxoisErrorsMessage } from "../../../../errorHandler/utils/errors.utils";
import useQueryURL from "../../../../../common/hooks/useQueryURL";
import { AxiosError } from "axios";
import useHardware from "../../../../hardwares/hooks/useHardware";

export interface NewChargePointFormPresenterProps {
  onSuccess: () => void;
  stationId?: string;
}

export interface NewChargePointData {
  name: string;
  hardwareId: string;
  stationId?: string;
}

export interface NewChargePointDataErrors {
  isErrorName: boolean;
  isErrorHardwareId: boolean;
  allGood: boolean;
  errorMessage: string | null | undefined;
}

const INITIAL_FORM_DATA: NewChargePointData = {
  name: "",
  hardwareId: "",
};

const INITIAL_ERRORS: NewChargePointDataErrors = {
  isErrorName: false,
  isErrorHardwareId: false,
  allGood: true,
  errorMessage: null,
};

const convertNewChargePointDataToChargePoint = (
  data: NewChargePointData
): ChargePointPatch => {
  return {
    charge_point_name: data.name,
    charge_point_hardware_id: data.hardwareId,
    charge_point_station: data.stationId,
  };
};

const checkValidityForm = (
  data: ChargePointPatch,
  userRole: UserRole | undefined
): NewChargePointDataErrors => {
  const errorState = { ...INITIAL_ERRORS };
  let errorMessage = [];

  if (!userRole) {
    errorState.allGood = false;
    errorMessage.push(
      "Vous n'avez pas les droits pour créer un point de charge"
    );
  }

  if (!inputValidityChecker.validateNotEmptyText(data.charge_point_name)) {
    errorState.isErrorName = true;
    errorState.allGood = false;
    errorMessage.push("Nom est obligatoire");
  }

  if (
    !inputValidityChecker.validateNotEmptyText(data.charge_point_hardware_id)
  ) {
    errorState.isErrorHardwareId = true;
    errorState.allGood = false;
    errorMessage.push("Device ID est obligatoire");
  }

  if (!inputValidityChecker.validateNotEmptyText(data.charge_point_station)) {
    errorState.allGood = false;
    errorMessage.push("Station est obligatoire");
  }
  if (errorState.allGood === false) {
    errorState.errorMessage = errorMessage.join(", ").concat(".");
  }

  return errorState;
};

const tradAxiosError = (axiosError: AxiosError): NewChargePointDataErrors => {
  const errorState = { ...INITIAL_ERRORS };
  errorState.allGood = false;

  const messageAxios = extractAxoisErrorsMessage(axiosError);

  const messageList = [];

  if (messageAxios.includes("CODE_ERROR_APP:3-2")) {
    messageList.push("Device ID existe déjà");
    errorState.isErrorHardwareId = true;
    errorState.allGood = false;
  } else {
    messageList.push(messageAxios);
  }

  if (messageList.length > 0) {
    errorState.errorMessage = messageList.join(", ").concat(".");
  }

  return errorState;
};

export default function NewChargePointFormPresenter({
  onSuccess,
  stationId,
}: NewChargePointFormPresenterProps) {
  const { createNewBorneQuery, stationIdFound } = useChargePoints({
    stationId,
  });
   const { findHardwareByDevUID} = useHardware();
  const { userRole } = useAuth();
  const [formData, setFormData] = useState<NewChargePointData>({
    ...INITIAL_FORM_DATA,
    stationId: stationIdFound,
  });
  const [errors, setErrors] =
    useState<NewChargePointDataErrors>(INITIAL_ERRORS);
  const [messageError, setMessageError] = useState<string | null | undefined>(
    null
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { setQueryValue } = useQueryURL();

  const handleSubmit = async () => {
    const newChargePoint = convertNewChargePointDataToChargePoint(formData);
    const errors = checkValidityForm(newChargePoint, userRole);


    
      setIsLoading(true);
      const hardwareFound  = await findHardwareByDevUID(newChargePoint.charge_point_hardware_id || "none");

      setIsLoading(false);

      if (!hardwareFound){
        errors.isErrorHardwareId = true;
        errors.allGood = false;
        errors.errorMessage = "Device ID n'existe pas";
      }
      newChargePoint.charge_point_hardware_id = hardwareFound?.hardware_id || "";

    if (errors.allGood) {
      createNewBorneQuery.mutate({
        newData: newChargePoint,
        callback: ({ result, error }) => {
          if (error) {
            const errorState = tradAxiosError(error);
            setErrors(errorState);
            setMessageError(errorState.errorMessage);
          }
          if (result) {
            onSuccess();
            setFormData({ ...INITIAL_FORM_DATA, stationId: stationIdFound });
            setQueryValue({
              singleChargePoint: "true",
              chargePointId: result.charge_point_id,
            });
          }
        },
      });
    } else {
      setErrors(errors);
      setMessageError(errors.errorMessage);
    }
  };

  const handleChange = (
    key: keyof NewChargePointData,
    value: string | number
  ) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };
  return (
    <NewChargePointFormView
      errorStatus={errors}
      messageError={messageError}
      setFormData={handleChange}
      formData={formData}
      handleSubmit={handleSubmit}
      currentUserRoleCreator={userRole}
      isLoading={createNewBorneQuery.isLoading || isLoading}
    />
  );
}
